import { useEffect, useMemo, useState } from 'react';
import '../styles/joke-toast.css';

const VISIBLE_MS = 6000;
const OUT_MS = 250;

// 5 manga variants, picked at random per fire so the agent gets visual
// variety without ever seeing the variant name labelled. The user
// specifically asked for no design / type names rendered, so the kaboom
// star has no inner text and there is no bucket caption anywhere.
const VARIANTS = [1, 2, 3, 4, 5];

// Star ornament — multi-pointed manga burst, no inner text per design
// direction. Fill colour varies per variant.
function Kaboom({ fill, stroke }) {
  return (
    <svg className="joke-kaboom" viewBox="0 0 64 64" aria-hidden="true">
      <polygon
        points="32,2 38,22 60,18 44,34 60,50 38,46 32,62 26,46 4,50 20,34 4,18 26,22"
        fill={fill}
        stroke={stroke}
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// The engine wraps {taskName}, {hours}, {minutes} in <em>...</em> markers.
// Parse + render safely as React nodes (no dangerouslySetInnerHTML).
function HeadlineText({ html }) {
  const parts = useMemo(() => {
    const out = [];
    const re = /<em>(.*?)<\/em>/g;
    let last = 0;
    let m;
    while ((m = re.exec(html)) !== null) {
      if (m.index > last) out.push({ t: 'text', v: html.slice(last, m.index) });
      out.push({ t: 'em', v: m[1] });
      last = m.index + m[0].length;
    }
    if (last < html.length) out.push({ t: 'text', v: html.slice(last) });
    return out;
  }, [html]);
  return parts.map((p, i) =>
    p.t === 'em' ? <em key={i}>{p.v}</em> : <span key={i}>{p.v}</span>
  );
}

function V1({ joke }) {
  return (
    <div className="joke-card v1 in">
      <div className="joke-speedlines" />
      <div className="joke-halftone" />
      <Kaboom fill="#d8472b" stroke="#0d0d12" />
      <div className="joke-content">
        <h3 className="joke-headline"><HeadlineText html={joke.text} /></h3>
      </div>
    </div>
  );
}

function V2({ joke }) {
  return (
    <div className="joke-card v2 in">
      <div className="joke-speedlines" />
      <div className="joke-halftone" />
      <Kaboom fill="#ffd84d" stroke="#0d0d12" />
      <div className="joke-content">
        <h3 className="joke-headline"><HeadlineText html={joke.text} /></h3>
      </div>
      <svg className="joke-tail" viewBox="0 0 28 18" aria-hidden="true">
        <polygon points="0,0 28,0 14,18" fill="#fff" stroke="#1d2230" strokeWidth="3" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function V3({ joke }) {
  return (
    <div className="joke-card v3 in">
      <div className="joke-speedlines" />
      <div className="joke-halftone" />
      <Kaboom fill="#ffd84d" stroke="#d8472b" />
      <div className="joke-content">
        <h3 className="joke-headline"><HeadlineText html={joke.text} /></h3>
      </div>
    </div>
  );
}

function V4({ joke }) {
  return (
    <div className="joke-card v4 in">
      <div className="joke-art">!</div>
      <div className="joke-text-pane">
        <div className="joke-halftone" />
        <Kaboom fill="#d8472b" stroke="#0d0d12" />
        <h3 className="joke-headline"><HeadlineText html={joke.text} /></h3>
      </div>
    </div>
  );
}

function V5({ joke }) {
  return (
    <div className="joke-card v5 in">
      <div className="joke-art">HA HA</div>
      <div className="joke-text-pane">
        <div className="joke-halftone" />
        <Kaboom fill="#d8472b" stroke="#0d0d12" />
        <h3 className="joke-headline"><HeadlineText html={joke.text} /></h3>
      </div>
    </div>
  );
}

const RENDERERS = { 1: V1, 2: V2, 3: V3, 4: V4, 5: V5 };

export default function JokeToast({ joke, onDismiss }) {
  const [closing, setClosing] = useState(false);
  // Pick a variant once per joke. Stays stable across re-renders so the
  // animation doesn't restart mid-life.
  const variant = useMemo(
    () => VARIANTS[Math.floor(Math.random() * VARIANTS.length)],
    [joke?.id]
  );

  useEffect(() => {
    if (!joke) return undefined;
    const t = setTimeout(() => {
      setClosing(true);
      setTimeout(onDismiss, OUT_MS);
    }, VISIBLE_MS);
    return () => clearTimeout(t);
  }, [joke?.id, onDismiss]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') {
        setClosing(true);
        setTimeout(onDismiss, OUT_MS);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const handleClick = () => {
    setClosing(true);
    setTimeout(onDismiss, OUT_MS);
  };

  if (!joke) return null;

  const Renderer = RENDERERS[variant] || V1;

  return (
    <div
      className="joke-root"
      onClick={handleClick}
      style={closing ? { opacity: 0, transition: `opacity ${OUT_MS}ms ease` } : undefined}
    >
      <Renderer joke={joke} />
    </div>
  );
}
